import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import PostCard from "@/components/feature/PostCard";
import { posts } from "@/mocks/posts";
import { zones } from "@/mocks/zones";

function getZoneName(zoneId: string) {
  return zones.find((zone) => zone.id === zoneId)?.name ?? "专区";
}

function formatReads(count: number) {
  return count >= 10000 ? `${(count / 10000).toFixed(1)}万` : `${count}`;
}

export default function LatestFeed() {
  const latest = [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));
  const hotList = [...posts].sort((a, b) => b.readCount - a.readCount).slice(0, 5);
  const recommended = posts.filter((post) => !post.featured).slice(2, 5);
  const allTags = Array.from(new Set(posts.flatMap((post) => post.tags))).slice(0, 10);

  return (
    <section aria-labelledby="feed-title" className="w-full bg-background-50 border-b border-background-200">
      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          id="feed-title"
          eyebrow="Feed"
          title="最新整理内容"
          description="编辑每天从各专区挑选值得看的条目重新整理，读一段摘要就能判断要不要点进去。"
          action={
            <span className="text-sm text-foreground-500">
              共 {posts.length} 条 · 每日持续更新
            </span>
          }
        />

        <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8 flex flex-col gap-4">
            {latest.map((post, index) => (
              <Reveal key={post.id} delay={index * 50}>
                <PostCard post={post} variant="list" />
              </Reveal>
            ))}
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-lg text-foreground-950">本周热榜</h3>
                <i className="ri-fire-fill text-accent-600"></i>
              </div>
              <ol className="mt-4 flex flex-col divide-y divide-background-200">
                {hotList.map((post, index) => (
                  <li key={post.id}>
                    <Link
                      to={`/post/${post.id}`}
                      className="group flex items-start gap-3 py-3 cursor-pointer"
                    >
                      <span
                        className={`font-label text-lg w-6 shrink-0 ${
                          index < 3 ? "text-primary-600" : "text-foreground-400"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="flex flex-col min-w-0">
                        <span className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {post.title}
                        </span>
                        <span className="mt-1 text-xs text-foreground-500 flex items-center gap-2">
                          <span>{getZoneName(post.zoneId)}</span>
                          <span className="flex items-center gap-0.5">
                            <i className="ri-eye-line"></i>
                            {formatReads(post.readCount)}
                          </span>
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
              <Link
                to="/ranking"
                className="mt-4 inline-flex items-center gap-1 text-sm text-primary-700 font-medium cursor-pointer hover:gap-2 transition-all"
              >
                查看完整榜单
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading font-bold text-lg text-foreground-950">热门标签</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <Link
                    key={tag}
                    to="/zones"
                    className="text-xs px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading font-bold text-lg text-foreground-950">编辑推荐</h3>
              <div className="mt-4 flex flex-col gap-4">
                {recommended.map((post) => (
                  <Link
                    key={post.id}
                    to={`/post/${post.id}`}
                    className="group flex gap-3 cursor-pointer"
                  >
                    <span className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={post.cover}
                        alt={`${post.title} 配图`}
                        title={`${post.title} - 爆料91专区合集`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="flex flex-col min-w-0">
                      <span className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {post.title}
                      </span>
                      <span className="mt-1 text-xs text-foreground-500">
                        {getZoneName(post.zoneId)} · {post.date}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}